
import { defineComponent, reactive, onBeforeMount, computed } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// @module components
import TimeSlotItem from '@/modules/TimeSlotsModule/components/TimeSlotItem/index.vue'
import TimeSlotChangeStatusModal from '@/modules/TimeSlotsModule/components/TimeSlotChangeStatusModal/index.vue'

// @types
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import type { TimeSlot } from '@/new-services/types/time-slots'
import type { Period } from '@/new-services/types/periods'

// @new-services
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import timeSlotsAPI from '@/new-services/time-slots.service'
import periodsAPI from '@/new-services/periods.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Loader,
        TheSelect,
        TimeSlotItem,
        TimeSlotChangeStatusModal
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TimeSlot[]
        })

        const filters_state = reactive({
            date: undefined as string | undefined,
            dispatcherSectorId: 24 as number | null,
            periodGroupId: 1 as number | null,
            typeId: 2
        })

        const change_status_state = reactive({
            is_opened: false,
            timeSlotId: null as number | null,
            statusId: null as number | null
        })

        const data_state = reactive({
            dispatcher_sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as DispatcherSector[]
            },
            periods: {
                is_loading: false,
                is_failed: false,
                list: [] as Period[]
            }
        })

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getDispatcherSectors({ page: 1, limit: 9999 })
                data_state.dispatcher_sectors.list = response.data.data

                data_state.dispatcher_sectors.is_failed = false
            } catch (error) {
                data_state.dispatcher_sectors.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении секторов диспетчера произошла ошибка' })
            } finally {
                data_state.dispatcher_sectors.is_loading = false
            }
        }

        const formatDate = (date: Date): string => date.toISOString().split('T')[0]

        const loadPeriods = async () => {
            try {
                data_state.periods.is_loading = true

                const currentDate = new Date(Date.now())
                const dateFrom = formatDate(currentDate)
                const dateTo = formatDate(new Date(currentDate.getFullYear() + 1, 1, 1, 12))

                const response = await periodsAPI.getPeriods({ dateFrom, dateTo, dispatcherSectorIds: filters_state.dispatcherSectorId ? [filters_state.dispatcherSectorId as number] : []  })
                data_state.periods.list = response.data.data

                data_state.periods.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении списка периодов произошла ошибка' })
                data_state.periods.is_failed = true
            } finally {
                data_state.periods.is_loading = false
            }
        }

        const loadTimeSlots = async () => {
            try {
                state.is_loading = true

                const response = await timeSlotsAPI.getTimeSlots({
                    dispatcherSectorId: filters_state.dispatcherSectorId,
                    periodGroupId: filters_state.periodGroupId
                })

                state.list = response.data.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении тайм-слотов произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            const date = new Date(Date.now())
            filters_state.date = date.toISOString().split('T')[0]

            loadDispatcherSectors()
            loadTimeSlots()
            loadPeriods()
        })

        const onChangeTimeSlotStatus = (payload: { timeSlotId: number; statusId: number }) => {
            change_status_state.statusId = payload.statusId
            change_status_state.timeSlotId = payload.timeSlotId
            change_status_state.is_opened = true
        }

        const onCancelChangeTimeSlotStatus = () => {
            change_status_state.is_opened = false
            change_status_state.statusId = null
            change_status_state.timeSlotId = null
        }

        const onTimeSlotStatusChanged = () => {
            onCancelChangeTimeSlotStatus()
            loadTimeSlots()
        }

        const listByType = computed(() => {
            return state.list.filter((item) => item.typeId === filters_state.typeId)
        })

        return {
            state,
            filters_state,
            change_status_state,
            data_state,
            loadDispatcherSectors,
            loadTimeSlots,
            loadPeriods,
            onChangeTimeSlotStatus,
            onCancelChangeTimeSlotStatus,
            onTimeSlotStatusChanged,
            listByType
        }
    }
})
